import { effect, inject, Injectable, untracked } from '@angular/core'
import { catchError, combineLatestWith, finalize, Observable, of, shareReplay, switchMap, take, throwError } from 'rxjs'
import {
   AccountsService,
   AppSignals,
   AuthService,
   CommonService
   //HybridWebviewInteropService
} from '@appShared/services'
import { ISiteConfiguration, IUser } from '@appShared/interfaces/[Interface-based]'
import { retryTransient } from '@appShared/utils/retry-transient'
import { setErrorMessage } from '@appShared/utils/error-message'
import { PRESERVE_HTTP_ERROR } from '@appShared/services/http-request-interceptor'
import { IProfile, ISubscription } from '@appShared/interfaces/[Model-based]'
import { SITE_DIRECTORY_ROUTES } from '@appShared/site-directory.routes'
import { environment } from '@appEnvironments/environment'
import { HttpClient, HttpContext } from '@angular/common/http'

@Injectable({
   providedIn: 'root'
})
export class AppFacade {
   private _httpClient = inject(HttpClient)
   private _authService = inject(AuthService)
   private _accountsService = inject(AccountsService)
   private _commonService = inject(CommonService)
   private _appSignals = inject(AppSignals)
   private _appState = this._appSignals.appState
   //private _hybridIsLoggingOut = this._appSignals.hybridIsLoggingOut
   //private _hybridWebviewInteropService = inject(HybridWebviewInteropService)
   private _isDevTest = !environment.production

   constructor() {
      effect(() => {
         let msalIsLoggedIn = this._appSignals.msalIsLoggedIn()

         untracked(() => {
            const currentUser = this._appSignals.currentUser()

            //handling timing issue (usually when returning session),
            // where site appears logged out because msal hasn't reported yet
            // - if no user in state, but logged in, refreshCurrentUser
            if (msalIsLoggedIn && !!!currentUser) {
               if (this._isDevTest) {
                  console.log('effect msalIsLoggedIn - but no user, calling refreshCurrentUser', (new Date()).getTime())
               }
               this.refreshCurrentUser().subscribe({
                  error: () => { /* background refresh - guards own the error UX */ }
               })
            }
         })
      })

      // this will trigger effect (above) so
      // that _integrateHybrid will get called  clear user on app
      //effect(() => {
      //   const user = this._appSignals.currentUser()
      //   const hybridIsLoggingOut = this._hybridIsLoggingOut()

      //   untracked(() => {
      //      this._integrateHybrid(user)

      //      //if no user, and in APP is logging out - call logout
      //      if (hybridIsLoggingOut && !!!user) {
      //         this._appSignals.setCurrentUser(null)

      //         this._authService.logout()
      //      }
      //   })
      //}, { allowSignalWrites: true })
   }

   /*
    private methods
   */

   private _routesPrepared = false
   private _prepareRouteUris(): Observable<boolean> {
      if (this._routesPrepared) return of(true)

      /* iterative method to loop over route objects
         to create usuable uris for ease of routing
         and no need to concatenate routes in code
      */

      let setConditionalUri = (parentUri?: any, uri?: any): string => {
         return parentUri
            ? uri
               ? `${parentUri}/${uri}`
               : parentUri
            : uri
               ? uri
               : ''
      }

      let setUrl = (obj: any, parentBaseUri?: any): void => {
         let baseUri = obj.baseUri

         Object.entries(obj)
            .forEach(([key, value]) => {
               if (typeof value === "object") {

                  // build "baseUri" to pass to children objects
                  baseUri = setConditionalUri(parentBaseUri, obj.baseUri)

                  setUrl(value, baseUri)

               } else if (key === 'uri') {

                  // set uri based on parent routes
                  obj[key] = setConditionalUri(parentBaseUri, value)
               }
            })
      }

      let routes = SITE_DIRECTORY_ROUTES

      setUrl(routes)

      //let siteRoutes = { ...routes } as SiteRouteModel

      //this.siteRoutes.update((siteDirectory) => ({
      //   ...siteDirectory,
      //   siteRoutes
      //}))

      this._routesPrepared = true
      return of(true)
   }

   /*
    public methods
   */

   initializeSite(): Observable<boolean> {
      if (this._isDevTest) {
         console.log('initializeSite: getting config', (new Date()).getTime())
      }

      return this._httpClient.get<ISiteConfiguration>(`${environment.apiConfig.endpointUri}/config`, {
         context: new HttpContext().set(PRESERVE_HTTP_ERROR, true)
      }).pipe(
         retryTransient(),
         //combineLatestWith(this._accountsService.getCurrentUser()),
         combineLatestWith(this._prepareRouteUris())
      ).pipe(
         take(1),
         //switchMap(([[siteConfig, currentUser], routesPrepared]) => {
         switchMap(([siteConfig, routesPrepared]) => {

            // override our environment base
            if (siteConfig.siteUri) {
               environment.baseUri = siteConfig.siteUri
            }


            if (this._isDevTest) {
               console.log('initializeSite: setting config state', (new Date()).getTime())
               console.log('initializeSite: status of isLoggedIn', this._appSignals.msalIsLoggedIn(), (new Date()).getTime())
            }

            this._appSignals.setSiteConfig(siteConfig)

            //this._refreshCurrentUser.next(currentUser)

            return of(true)
         }),
         catchError((err) => {
            // err is the preserved HttpErrorResponse - store display text, not the object
            this._appSignals.setError(typeof err === 'string' ? err : setErrorMessage(err))

            return of(false)
         })
      )
   }

   private _refreshCurrentUserRequest$: Observable<IUser> | null = null

   refreshCurrentUser(returnCacheIfExist?: boolean): Observable<IUser> {
      if (this._isDevTest) {
         console.log('refreshCurrentUser', (new Date()).getTime())
      }

      if (returnCacheIfExist) {
         const user = this._appState().currentUser
         if (user) {
            if (this._isDevTest) {
               console.log('refreshCurrentUser:cached:user', user, (new Date()).getTime())
            }
            return of(user)
         }
      }

      // Coalesce concurrent callers (e.g., route guards + the msalIsLoggedIn
      // effect firing at the same moment) into a single in-flight request
      if (this._refreshCurrentUserRequest$) {
         return this._refreshCurrentUserRequest$
      }

      this._refreshCurrentUserRequest$ = this._accountsService.getCurrentUser().pipe(
         retryTransient(),
         take(1),
         switchMap(user => {
            this._appSignals.setCurrentUser(user)

            if (this._isDevTest) {
               console.log('refreshCurrentUser:user', user, (new Date()).getTime())
            }
            return of(user)
         }),
         catchError((err) => {
            if (this._isDevTest) {
               console.log('refreshCurrentUser:error', err, (new Date()).getTime())
            }

            // Rethrow (never EMPTY) so route guards waiting on this observable
            // see the failure — completing without emitting silently cancels
            // the navigation and leaves the user stuck on the auth spinner.
            // A null emission is reserved for a 204 (no profile) response.
            return throwError(() => err)
         }),
         // Clear the in-flight handle once settled (finalize sits upstream of
         // shareReplay so it runs once, not per subscriber); later callers
         // start a fresh request instead of replaying a stale result
         finalize(() => this._refreshCurrentUserRequest$ = null),
         shareReplay(1)
      )

      return this._refreshCurrentUserRequest$
   }

   setUserMustResetPassword(mustResetPassword?: boolean): void {

      const user = this._appState().currentUser
      const currentUser = {
         ...user,
         mustResetPassword: mustResetPassword || false
      }

      this._appSignals.setCurrentUser(currentUser)
   }

   navigateToExternal(uri?: string, targetBlank?: boolean) {
      if (!uri) return

      if (this._isDevTest) {
         console.log('navigateToExternal: redirecting to:', uri)
      }

      if (targetBlank) {
         window.open(uri, '_blank')
         return
      }

      this._commonService.setLoading(true)
      window.location.replace(uri)
   }

   //private _integrateHybrid(user?: IUser): void {

   //   console.log('integrating user in hybrid service:', user?.displayName)
   //   if (!user) {
   //      this._hybridWebviewInteropService.clearUserInfo()
   //      return
   //   }

   //   const contact = user.profile?.contact
   //   if (contact) {
   //      this._hybridWebviewInteropService.integrateUserInfo(contact)
   //   } else {
   //      this._hybridWebviewInteropService.clearUserInfo()
   //   }
   //}
}
